import { useEffect, type ReactNode } from 'react';
import { authApi } from './authApi';
import { useAuthStore } from '@/store/authStore';
import { SplashScreen } from '@/components/feedback/SplashScreen';

/**
 * Resolves the session on first load. The access token is never persisted, so we
 * rehydrate it from the httpOnly refresh cookie: rotate the token, then fetch the
 * profile. Any failure leaves the app cleanly unauthenticated (spec §3).
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const status = useAuthStore((s) => s.status);

  useEffect(() => {
    let active = true;

    async function bootstrap() {
      try {
        const { accessToken, expiresIn } = await authApi.refresh();
        if (!active) return;
        useAuthStore.getState().setAccessToken(accessToken, expiresIn);
        const user = await authApi.me();
        if (!active) return;
        useAuthStore.getState().setSession({ accessToken, expiresIn, user });
      } catch {
        if (active) useAuthStore.getState().clear();
        useAuthStore.getState().markBootstrapped();
      }
    }

    void bootstrap();
    return () => {
      active = false;
    };
  }, []);

  if (status === 'bootstrapping') {
    return <SplashScreen />;
  }

  return <>{children}</>;
}
